import './styles.scss';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import track from 'react-tracking';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
} from 'gatsby-plugin-react-intl';

import SpeedCurveSnippet from '@prospectus/common/ui/SpeedCurveSnippet';
import { SPEEDCURVE_LABEL_EDX_PROGRAM_ABOUT } from '@utils/speedCurve';
import { intlShape } from '@prospectus/common/types/intlShape';
import Layout from '@prospectus/common/ui/Layout';
import ProgramHeader from '@prospectus/program-page/components/ProgramHeader';
import ProgramDataBar from '@prospectus/program-page/components/ProgramDataBar';
import ProgramMain from '@prospectus/program-page/components/ProgramMain';
import AboutEdx from '@prospectus/program-page/components/AboutEdx';
import SubjectTopics from '@prospectus/program-page/components/SubjectTopics';
import SEOPage from '@prospectus/seo/ui/SEOPage';
import AboutRouting from '@prospectus/program-page/components/AboutRouting';
import programShape from '@prospectus/common/types/programShape';
import { allCurrencyShape } from '@prospectus/common/types/allCurrencyShape';
import { formatProgramPrice } from '@utils/formatting';
import { getCourseSchemaOrgJsonLD } from '@utils/program';

import { formatProgramType } from '@utils/edxProductDisplay';
import { subjectsForTopics } from '@utils/topics';
import { submitEvent } from '@utils/analytics';
import { WebviewContext } from '@utils/context';
import { getOverriddenFields, seoContentTypeShape, customMetaTagShape } from '@utils/seo';
import { withLocation } from '@utils/navigation';

import { getUserLoggedIn } from '@data/actions/user';
import withUser from '@utils/withUser';
import setCurrentPage from '@data/actions/currentPage';
import {
  PROFESSIONAL_CERTIFICATE_ABOUT_PAGE,
  MICRO_MASTERS_ABOUT_PAGE,
  MICRO_BACHELORS_ABOUT_PAGE,
} from '@utils/pageTypes';
import { PRODUCT_MAP_EDX } from '@utils/discoveryConstants';
import withUserEnrollment from '../../../../source-utils/withUserEnrollment';
import { fetchUserSubscriptionStatus } from '../../api';
import { USER_SUBSCRIPTION_STATES } from '../../../../utils/constants';

function ProgramDetailPage({
  location,
  pageContext: {
    program,
    allCurrency,
    webview,
    customSeoFields,
    customMetaTags,
    linkedProgram,
  },
  intl,
  tracking,
  userFullyEnrolled,
  requestUserLoggedIn,
  userLoggedIn,
  setPage,
}) {
  const [userSubscriptionState, setUserSubscriptionState] = useState('');
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);

  const submitViewedEvent = () => {
    const {
      title,
      programOriginalPrice,
      programPrice,
      subscriptionEligible,
      subscriptionPrices,
    } = program;

    const getSubscriptionPriceDetails = () => subscriptionPrices.find((item) => item.currency === 'USD');

    const isSubscriptionEnabled = !!subscriptionEligible && !webview;

    const priceDetails = getSubscriptionPriceDetails();
    const price = formatProgramPrice(priceDetails?.price);

    const subscriptionProps = isSubscriptionEnabled
      ? {
        isSubscription: true,
        programOriginalPrice: price,
        programPrice: price,
      }
      : {};

    let discountedPercentage;
    if (programPrice && programOriginalPrice) {
      discountedPercentage = 100
        - (parseInt(programPrice, 10) * 100) / parseInt(programOriginalPrice, 10);
    }
    const logoUrl = program.authoringOrganizations[0].logoImageUrl;
    const componentProps = {
      nonInteraction: 1,
      programOriginalPrice,
      programPrice,
      discountedPercentage: `%${discountedPercentage}`,
      title,
      logoUrl,
      ...subscriptionProps,
    };

    submitEvent(tracking, componentProps, 'edx.bi.program-about-page.viewed');
  };

  const getUserSubscriptionState = async (uuid) => {
    setIsSubscriptionLoading(true);
    const subscriptionStatus = await fetchUserSubscriptionStatus(uuid);
    if (subscriptionStatus) {
      setUserSubscriptionState(subscriptionStatus);
      setIsSubscriptionLoading(false);
    } else {
      setIsSubscriptionLoading(false);
    }
  };

  const checkSubscriptionStatus = async () => {
    const { subscriptionEligible, uuid } = program;

    const isSubscriptionEnabled = !!subscriptionEligible && !userFullyEnrolled && !webview;

    if (isSubscriptionEnabled) {
      getUserSubscriptionState(uuid);
    }
  };

  useEffect(() => {
    const { type } = program;
    if (type === PRODUCT_MAP_EDX.PROFESSIONAL_CERTIFICATE.NAME) {
      setPage(PROFESSIONAL_CERTIFICATE_ABOUT_PAGE);
    }
    if (type === PRODUCT_MAP_EDX.MICRO_MASTERS.NAME) {
      setPage(MICRO_MASTERS_ABOUT_PAGE);
    }
    if (type === PRODUCT_MAP_EDX.MICRO_BACHELORS.NAME) {
      setPage(MICRO_BACHELORS_ABOUT_PAGE);
    }

    submitViewedEvent();
    requestUserLoggedIn();
  }, []);

  useEffect(() => {
    if (userLoggedIn) {
      checkSubscriptionStatus();
    }
  }, [userLoggedIn]);

  const getHook = () => {
    const { marketingHook, type } = program;

    if (marketingHook) {
      return marketingHook;
    }

    return (
      <FormattedMessage
        id="prospectus.template.program.default_hook"
        description="Default text hook for a program based in program type."
        defaultMessage={'{programType, select, '
          + 'micromasters {Accelerate your Master\'s Degree} '
          + 'xseries {Develop deep knowledge} '
          + 'other {Drive your career forward}}'}
        values={{
          programType: type.toLowerCase(),
        }}
      />
    );
  };

  const getSEOTitle = () => {
    const { title, uuid, type } = program;

    const updatedTitles = [
      {
        uuid: 'ee71fbd5-1479-41a8-b11e-074d4028dd6b',
        title: 'IBM Data Science Professional Certificate',
      },
      {
        uuid: '3c32e3e0-b6fe-4ee4-bd4f-210c6339e074',
        title: 'HarvardX Data Science Professional Certificate',
      },
      {
        uuid: '87ef0a37-88f5-4843-888c-166fc9d1644a',
        title: 'Agile Project Management Professional Certificate',
      },
    ];

    const programOverride = updatedTitles.find((item) => item.uuid === uuid);

    if (programOverride) {
      return programOverride.title;
    }

    const messageId = 'prospectus.program_details.title';

    const messages = defineMessages({
      [messageId]: {
        id: messageId,
        description: "Adds 'Program Type' and 'Program Title' to Program Details Title with necessary trademark and possessive punctuation",
        defaultMessage: ('{typeKey, select, '
          + 'micromasters {{title} {programType}® Program} '
          + 'masters {{title} Master\'s Degree Program} '
          + 'xseries {{title} {programType} Program} '
          + 'microbachelors {{title} {programType}® Program}'
          + 'professional_certificate {{title} Professional Certificate}'
          + 'other {{title} {programType}}}'),
      },
    });

    return intl.formatMessage(messages[messageId], {
      typeKey: type.toLowerCase().replace(' ', '_'),
      programType: type,
      title,
    });
  };

  const getPageSEO = (overrides) => {
    const { pathname } = location;

    const defaultSEOProps = {
      title: overrides.title || getSEOTitle(),
      description: overrides.description || program.stripedSubtitle || program.subtitle,
      pathname: overrides.pathname || pathname,
      image: program.cardImageUrl || '',
    };

    if (webview || program.status === 'unpublished') {
      return {
        ...defaultSEOProps,
        robots: 'noindex, nofollow',
      };
    }

    const { robotsMetaTagContent } = overrides;
    if (robotsMetaTagContent) {
      return {
        ...defaultSEOProps,
        robots: robotsMetaTagContent,
      };
    }

    return defaultSEOProps;
  };

  /**
   *  We remove all spaces between the words since one program has 2
   *  spaces between Data and Science to differentiate itself from the
   *  program with only one space between Data and Science. Yep, it's bad.
   */
  const formatProgramTitle = () => {
    const { title, type } = program;

    return title.replace(/ /g, '') === 'DataScience' ? (
      <>
        {title} {formatProgramType(type)}
      </>
    ) : (
      title
    );
  };

  const { subscriptionEligible, subscriptionPrices } = program;

  const getSubscriptionPriceDetails = () => subscriptionPrices.find((item) => item.currency === 'USD');

  const isUserFullyEnrolledWithoutSubscription = !!userFullyEnrolled
    && userSubscriptionState === USER_SUBSCRIPTION_STATES.NOT_STARTED;

  const userSubscriptionInfo = {
    isSubscriptionEnabled:
      !!subscriptionEligible
      && !isUserFullyEnrolledWithoutSubscription
      && !webview,
    priceDetails: getSubscriptionPriceDetails(),
    subscriptionStatus: userSubscriptionState,
  };

  // Use the first subject as the primary subject
  const primarySubject = program.subjects?.at(0);
  const primarySubjectSlug = primarySubject
    ? primarySubject.slug
    : '';

  const enrollButtonId = 'program-enrolling-now-section-btn';
  const courseFocusId = 'course-section-header';

  const cleanedProgramCourses = program.courses.map(({ course }) => course);
  // For program track selection
  const cleanedLinkedProgramCourses = linkedProgram
    ? linkedProgram.courses.map(({ course }) => course)
    : null;
  const linkedProgramWithCleanedCourses = linkedProgram
    ? { ...linkedProgram, courses: cleanedLinkedProgramCourses }
    : null;

  const overrides = getOverriddenFields(intl, customSeoFields, customMetaTags);
  const pageSEOProps = getPageSEO(overrides);
  const courseJsonLD = getCourseSchemaOrgJsonLD(program);

  return (
    <Layout
      title={pageSEOProps.title}
      pageClassname="program-details-page"
      allCurrency={allCurrency}
      webview={webview}
      pageType="Program"
    >
      {courseJsonLD ? (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(courseJsonLD)}
          </script>
        </Helmet>
      ) : null}
      <SpeedCurveSnippet pageLabel={SPEEDCURVE_LABEL_EDX_PROGRAM_ABOUT} />
      <WebviewContext.Provider value={webview}>
        <ProgramHeader
          {...program}
          primarySubject={primarySubject}
          programHook={getHook()}
          enrollButtonId={enrollButtonId}
          courses={cleanedProgramCourses}
          subscriptionDetails={userSubscriptionInfo}
          isSubscriptionLoading={isSubscriptionLoading}
        />
        <ProgramDataBar
          {...program}
          primarySubject={primarySubject}
          isBundled={program.isProgramEligibleForOneClickPurchase}
          enrollButtonId={enrollButtonId}
          courseFocusId={courseFocusId}
          enrollFocus={!webview}
          webview={webview}
          subscriptionDetails={userSubscriptionInfo}
        />
        <ProgramMain
          {...program}
          primarySubject={primarySubject}
          enrollButtonId={enrollButtonId}
          courseFocusId={courseFocusId}
          courses={cleanedProgramCourses}
          showPrice={!webview}
          linkedProgram={linkedProgramWithCleanedCourses}
          subscriptionDetails={userSubscriptionInfo}
          isSubscriptionLoading={isSubscriptionLoading}
        />
        {primarySubjectSlug
        && subjectsForTopics.includes(primarySubjectSlug) ? (
          <AboutRouting primarySubject={primarySubject} />
          ) : (
            <SubjectTopics {...program} title={() => formatProgramTitle()} />
          )}
        <AboutEdx />
        <SEOPage meta={pageSEOProps} />
      </WebviewContext.Provider>
    </Layout>
  );
}

ProgramDetailPage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string,
  }).isRequired,
  pageContext: PropTypes.shape({
    program: PropTypes.shape(programShape),
    allCurrency: allCurrencyShape.isRequired,
    webview: PropTypes.bool,
    customSeoFields: PropTypes.arrayOf(seoContentTypeShape),
    customMetaTags: PropTypes.arrayOf(customMetaTagShape),
    linkedProgram: PropTypes.shape(programShape),
  }).isRequired,
  intl: intlShape.isRequired,
  tracking: PropTypes.shape().isRequired,
  userFullyEnrolled: PropTypes.bool.isRequired,
  requestUserLoggedIn: PropTypes.func,
  userLoggedIn: PropTypes.bool,
  setPage: PropTypes.func,
};

ProgramDetailPage.defaultProps = {
  requestUserLoggedIn: () => {},
  userLoggedIn: false,
  setPage: () => {},
};

const mapStateToProps = (store) => ({
  userLoggedIn: store.userLoggedIn,
});

const mapDispatchToProps = (dispatch) => ({
  requestUserLoggedIn() {
    dispatch(getUserLoggedIn());
  },
  setPage(page) {
    dispatch(setCurrentPage(page));
  },
});

const ProgramDetailPageConnected = connect(mapStateToProps, mapDispatchToProps)(ProgramDetailPage);

export default track(({ pageContext, location }) => ({
  page: 'program',
  subject: pageContext.program.subjects[0]
    ? pageContext.program.subjects[0].slug
    : '',
  program_uuid: pageContext.program.uuid,
  mobile: false,
  path: location.pathname,
  url: location.href,
}))(withUser(withUserEnrollment(withLocation(injectIntl(ProgramDetailPageConnected)))));
